/**
 * Client-side filtering for the "My Submissions" history view.
 *
 * Two dimensions combine with AND: an asset selector and a time period (All /
 * Today / Last 7 days / Custom range). The logic is pure and `now`-injectable so
 * the calendar boundaries (today, the 7-day window, custom range edges) can be
 * exercised deterministically in tests. Day comparisons use the local calendar
 * day to match what `<input type="date">` and the user actually see.
 */
import type { AssetId } from '@/types'
import { parseDateToTimestamp } from './time'

export type TimePeriod = 'all' | 'today' | '7d' | 'custom'

/** Sentinel asset value meaning "no asset filter". */
export const ALL_ASSETS = 'all' as const
export type AssetFilterValue = AssetId | typeof ALL_ASSETS

export interface DateRange {
  /** Inclusive lower bound as a local `YYYY-MM-DD` day, or '' for unbounded. */
  from: string
  /** Inclusive upper bound as a local `YYYY-MM-DD` day, or '' for unbounded. */
  to: string
}

export interface SubmissionFilter {
  assetId: AssetFilterValue
  period: TimePeriod
  range: DateRange
}

/** The minimal shape filtering needs — satisfied by both Submission and DraftRecording. */
export interface FilterableRecording {
  assetId: AssetId
  createdAt: string
}

export const EMPTY_RANGE: DateRange = { from: '', to: '' }

const MS_DAY = 86_400_000

/** Local calendar day (`YYYY-MM-DD`) for a timestamp — aligns with date inputs. */
export function toLocalISODate(timestamp: number): string {
  const d = new Date(timestamp)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Local `YYYY-MM-DD` for `now` shifted back by whole days — for range presets. */
export function localDateDaysAgo(days: number, now: number = Date.now()): string {
  return toLocalISODate(now - days * MS_DAY)
}

function startOfLocalDay(timestamp: number): number {
  const d = new Date(timestamp)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/** True when any dimension is narrowing the list (drives the "Clear filters" affordance). */
export function isSubmissionFilterActive(filter: SubmissionFilter): boolean {
  return filter.assetId !== ALL_ASSETS || filter.period !== 'all'
}

function matchesPeriod(createdAt: string, filter: SubmissionFilter, now: number): boolean {
  if (filter.period === 'all') {
    return true
  }
  const ts = parseDateToTimestamp(createdAt)
  if (Number.isNaN(ts)) {
    return false
  }

  if (filter.period === 'custom') {
    const day = toLocalISODate(ts)
    if (filter.range.from && day < filter.range.from) return false
    if (filter.range.to && day > filter.range.to) return false
    return true
  }

  const rowDay = startOfLocalDay(ts)
  const todayStart = startOfLocalDay(now)
  if (filter.period === 'today') {
    return rowDay === todayStart
  }
  // '7d' — the last 7 calendar days, today inclusive.
  return rowDay >= todayStart - 6 * MS_DAY
}

/** Apply the asset + time-period filter, preserving the input order. */
export function filterSubmissions<T extends FilterableRecording>(
  rows: readonly T[],
  filter: SubmissionFilter,
  now: number = Date.now(),
): T[] {
  return rows.filter((row) => {
    if (filter.assetId !== ALL_ASSETS && row.assetId !== filter.assetId) {
      return false
    }
    return matchesPeriod(row.createdAt, filter, now)
  })
}
